import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuditLog } from 'pages/AuditLogPage/hooks';
import { ERouteLinks } from 'models/route';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageTitle } from 'theme/styles';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import {
  AuditLogDetails,
  StyledFilterChipWrapper,
} from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Box, Typography } from '@mui/material';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import {
  accessoryLabels,
  AdministrationObjectTypes,
  ammunitionLabels,
  AuditLogModules,
  componentLabels,
  EAuditLogEvents,
  EAuditLogObjectTypes,
  EventTypes,
  getEventTypeColor,
  labels,
  LicencesObjectTypes,
} from 'pages/AuditLogPage/helpers';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import FirearmEventDetails from 'pages/AuditLogPage/AuditLogViewPage/FirearmEventDetails';
import TransactionEventDetails from 'pages/AuditLogPage/AuditLogViewPage/TransactionEventDetails';
import AdministrationRoleEventDetails from 'pages/AuditLogPage/AuditLogViewPage/AdministrationRoleEventDetails';
import AdministrationDictionaryEventDetails from 'pages/AuditLogPage/AuditLogViewPage/AdministrationDictionaryEventDetails';
import CustomFieldEventDetails from 'pages/AuditLogPage/AuditLogViewPage/CustomFieldEventDetails';
import LegalEntitiesEventDetails from 'pages/AuditLogPage/AuditLogViewPage/LegalEntitiesEventDetails';
import AncillariesEventDetails from 'pages/AuditLogPage/AuditLogViewPage/AncillariesEventDetails';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import AdministrationUserEventDetails from 'pages/AuditLogPage/AuditLogViewPage/AdministrationUserEventDetails';
import ApiKeyEventDetails from 'pages/AuditLogPage/AuditLogViewPage/ApiKeyEventDetails';
import { useLang } from 'models/langContext';
import { FilterChip } from 'components/atoms/FilterChip';
import { useDictionary } from 'models/dictionaryContext';
import { prepareFilters } from 'helpers/filters';
import { EArtAccessoryType, EArtAncillaryType } from 'constants/ArtAccessoryType';
import { parseMultipleDictionaries } from 'helpers/dictionary';
import { useUsers } from 'pages/UsersPage/hooks';
import { IFormElementOptionProp } from 'models/form';
import { ECustomFieldType } from 'models/customField';
import { mergeDictionaries } from 'components/organisms/Dictionaries';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import GlobalSearchEventDetails from './GlobalSearchEventDetails';
import LicencesEventDetails from './LicencesEventDetails';
import AdministrationLoginLogosEventDetails from './AdministrationLoginLogosEventDetails';
import AdministrationSystemSettingsEventDetails from './AdministrationSystemSettingsEventDetails';
import AdministrationPasswordChange from './AdministrationPasswordChange';
import LegalEntityLicencesEventDetails from './LegalEntityLicencesEventDetails';
import { StyledRouterLink } from 'components/atoms/RouterLink/styles';
import PhotoGalleryEventDetails from './PhotoGalleryEventDetails';
import LicencesApplicationProcessEventDetails from './LicencesApplicationProcessEventDetails';
import { capitalizeFirstLetter } from 'utils/capitalize';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import LegalEntityLicencesPrintoutEventDetails from './LegalEntityLicencesPrintoutEventDetails';
import LegalEntityLicencesPrintoutBatchEventDetails from './LegalEntityLicencesPrintoutBatchEventDetails';
import LegalEntityLicencesAuthorityEventDetails from './LegalEntityLicencesAuthorityEventDetails';
import { EBatcheslabels, EPrintoutslabels } from 'pages/LicencesPage/helpers';

interface IValues {
  [key: string]: any;
}

const AuditLogViewPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { getAuditLogItemData, auditLog } = useAuditLog();
  const { longDateFormat, getProperties, shortDateFormat } = useGlobalProperty();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const { samePageAlert, auditLogSamePageAlert, clearAlert } = useAlert();
  const { selectedLanguage, defaultLanguage } = useLang();
  const [dictionaries, setDictionaries] = useState<IValues>();
  const [userOptions, setUserOptions] = useState<IFormElementOptionProp[]>([]);
  const { users, getUsersData } = useUsers();
  const [validLabels, setValidLabels] = useState<IValues>(labels);

  useEffect(() => {
    getProperties();
    (async () => {
      await getUsersData(1, 9999, undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (users?.length) {
      const usersOptions = users.map((user) => ({
        label: `${user.firstName} ${user.lastName} (${user.displayUniqueId})`,
        value: user.uniqueId,
      }));
      setUserOptions(usersOptions);
    }
  }, [users]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (auditLog) {
      switch (auditLog.objectType) {
        case EAuditLogObjectTypes.WEAPON_LICENCE_CASE:
          return setValidLabels({
            ...labels,
            ...Elabels,
          });
        case EAuditLogObjectTypes.WEAPON_LICENCE_PRINTOUT:
          return setValidLabels(EPrintoutslabels);
        case EAuditLogObjectTypes.WEAPON_LICENCE_PRINT_BATCH:
          return setValidLabels(EBatcheslabels);
        case EAuditLogObjectTypes.ACCESSORY:
          return setValidLabels(accessoryLabels);
        case EAuditLogObjectTypes.AMMUNITION:
          return setValidLabels(ammunitionLabels);
        case EAuditLogObjectTypes.COMPONENT:
          return setValidLabels(componentLabels);
        case EAuditLogObjectTypes.LE_INDIVIDUAL:
        case EAuditLogObjectTypes.LE_ORGANIZATION:
          return setValidLabels({
            ...labels,
            statusUniqueId: 'legalEntities.individual.accordion.legalEntityStatus.label',
          });
        default:
          setValidLabels(labels);
      }
    }
  }, [auditLog]);

  const {
    gender,
    getGender,
    getLegalVisibility,
    legalVisibility,
    legalEntityPrefix,
    getLegalEntityPrefix,
    getOrganisationCategory,
    organisationCategory,
    getOrganisationStateType,
    organisationStateType,
    getOrganisationMilitaryType,
    organisationMilitaryType,
    getOrganisationPoliceType,
    organisationPoliceType,
    getOrganisationCivilianType,
    organisationCivilianType,
    getState,
    state,
    legality,
    getLegality,
    artAccessoryType,
    getArtAccessoryType,
    artAccessoryManufacturerMagazine,
    getArtAccessoryManufacturerMagazine,
    artAccessoryManufacturerLaserLightModule,
    getArtAccessoryManufacturerLaserLightModule,
    artAccessoryManufacturerOpticalSight,
    getArtAccessoryManufacturerOpticalSight,
    artAccessoryManufacturerFlashEliminator,
    getArtAccessoryManufacturerFlashEliminator,
    artAccessoryManufacturerSuppressor,
    getArtAccessoryManufacturerSuppressor,
    artAmmunitionFunctionalType,
    artAmmunitionProjectileShape,
    getArtAmmunitionProjectileShape,
    getArtAmmunitionFunctionalType,
    artAccessoryHostType,
    getArtAccessoryHostType,
    artAccessoryMagnification,
    getArtAccessoryMagnification,
    artAccessoryUtility,
    getArtAccessoryUtility,
    artAccessoryReticle,
    getArtAccessoryReticle,
    artAccessoryLightColour,
    getArtAccessoryLightColour,
    artAccessoryComposition,
    getArtAccessoryComposition,
    artAccessoryProofHouse,
    getArtAccessoryProofHouse,
    artComponentProofHouse,
    getArtComponentProofHouse,
    artAccessoryCalibre,
    getArtAccessoryCalibre,
    artAmmunitionCalibreGauge,
    getArtAmmunitionCalibreGauge,
    artComponentCalibre,
    getArtComponentCalibre,
    artAncillaryOptions,
    getArtAncillaryOptions,
    artAmmunitionType,
    getArtAmmunitionType,
    artComponentType,
    getArtComponentType,
    artAmmunitionManufacturer,
    getArtAmmunitionManufacturer,
    getArtComponentManufacturer,
    artComponentManufacturer,
    transactionType,
    getTransactionType,
    getAuditLogModule,
    auditLogModule,
    getAuditLogEventType,
    auditLogEventType,
    getAuditLogEventName,
    auditLogEventName,
    artAmmunitionFactory,
    getArtAmmunitionFactory,
    artAmmunitionMunitionVelocity,
    getArtAmmunitionMunitionVelocity,
    artAmmunitionCartridgeComposition,
    getArtAmmunitionCartridgeComposition,
    artAmmunitionMunitionType,
    getArtAmmunitionMunitionType,
    artAmmunitionShotSize,
    getArtAmmunitionShotSize,
    artAmmunitionColourOfPrimerSealant,
    getArtAmmunitionColourOfPrimerSealant,
    getWeaponLicenceStatus,
    weaponLicenceStatus,
    getLegalType,
    legalType,
    getLegalStatus,
    legalStatus,
    getArtComponentCapacity,
    artComponentCapacity,
    getArtComposition,
    artComposition,
    getWeaponLicenceTemplateStatus,
    weaponLicenceTemplateStatus,
    getWeaponLicenceCaseStatus,
    weaponLicenceCaseStatus,
    getWeaponLicencingAuthority,
    weaponLicencingAuthority,
    getCaseProcessTypes,
    getWeaponLicencePrintStatus,
    weaponLicencePrintStatus,
    getWeaponLicencePrintBatchStatus,
    weaponLicencePrintBatchStatus,
    caseProcessTypes,
    make,
    getMake,
    manufacturer,
    getManufacturer,
    model,
    getModel,
    firearmType,
    getFirearmType,
  } = useDictionary();

  const getAccessoryManufacturerDictionaryData = (
    accessoriesTypeId: EArtAccessoryType,
    value: any,
  ) => {
    switch (accessoriesTypeId) {
      case EArtAccessoryType.MAGAZINES:
        return dictionaries?.artAccessoryManufacturerMagazine?.[value];
      case EArtAccessoryType.LASER_LIGHT_MODULES:
        return dictionaries?.artAccessoryManufacturerLaserLightModule?.[value];
      case EArtAccessoryType.OPTICAL_SIGHTS:
        return dictionaries?.artAccessoryManufacturerOpticalSight?.[value];
      case EArtAccessoryType.FLASH_ELIMINATORS:
        return dictionaries?.artAccessoryManufacturerFlashEliminator?.[value];
      case EArtAccessoryType.SUPPRESSORS:
        return dictionaries?.artAccessoryManufacturerSuppressor?.[value];
      default:
        return '';
    }
  };

  useEffect(() => {
    if (auditLog && auditLog.filteredByObjectValue) {
      getGender();
      getLegalVisibility();
      getLegalEntityPrefix();
      getOrganisationCategory();
      getOrganisationStateType();
      getOrganisationCivilianType();
      getOrganisationPoliceType();
      getOrganisationMilitaryType();
      getState();
      getLegality();
      getArtAccessoryType();
      getArtAccessoryManufacturerMagazine();
      getArtAccessoryManufacturerLaserLightModule();
      getArtAccessoryManufacturerOpticalSight();
      getArtAccessoryManufacturerFlashEliminator();
      getArtAccessoryManufacturerSuppressor();
      getArtAmmunitionFunctionalType();
      getArtAmmunitionProjectileShape();
      getArtAccessoryHostType();
      getArtAccessoryMagnification();
      getArtAccessoryUtility();
      getArtAccessoryReticle();
      getArtAccessoryLightColour();
      getArtAccessoryComposition();
      getArtAccessoryProofHouse();
      getArtAccessoryCalibre();
      getArtComponentProofHouse();
      getArtAmmunitionCalibreGauge();
      getArtComponentCalibre();
      getArtAncillaryOptions();
      getArtAmmunitionType();
      getArtAmmunitionColourOfPrimerSealant();
      getArtAmmunitionShotSize();
      getArtAmmunitionMunitionType();
      getArtAmmunitionCartridgeComposition();
      getArtAmmunitionMunitionVelocity();
      getArtComponentType();
      getArtAmmunitionManufacturer();
      getArtComponentManufacturer();
      getTransactionType();
      getAuditLogModule();
      getAuditLogEventType();
      getAuditLogEventName();
      getArtAmmunitionFactory();
      getWeaponLicenceStatus();
      getLegalType();
      getLegalStatus();
      getArtComponentCapacity();
      getArtComposition();
      getWeaponLicenceTemplateStatus();
      getWeaponLicenceCaseStatus();
      getWeaponLicencingAuthority();
      getCaseProcessTypes();
      getWeaponLicencePrintStatus();
      getWeaponLicencePrintBatchStatus();
      getMake();
      getManufacturer();
      getModel();
      getFirearmType();
    }
  }, [auditLog]);

  useEffect(() => {
    if (auditLog && auditLog.filteredByObjectValue) {
      setDictionaries({
        licenceAuthorityUniqueIds: prepareFilters(
          weaponLicencingAuthority,
          'uniqueId',
          'name',
        ),
        accountsUniqueIds: prepareFilters(userOptions, 'value', 'label'),
        genderUniqueId: prepareFilters(gender, 'uniqueId', 'name'),
        gendersUniqueIds: prepareFilters(gender, 'uniqueId', 'name'),
        visibilityUniqueId: prepareFilters(legalVisibility, 'uniqueId', 'name'),
        prefixGroupUniqueId: prepareFilters(legalEntityPrefix, 'uniqueId', 'name'),
        prefixGroupsUniqueIds: prepareFilters(legalEntityPrefix, 'uniqueId', 'name'),
        organisationCategoryUniqueId: prepareFilters(organisationCategory, 'uniqueId', 'name'),
        organisationCategoriesUniqueIds: prepareFilters(
          organisationCategory,
          'uniqueId',
          'name',
        ),
        organizationTypeUniqueId: parseMultipleDictionaries(
          [
            organisationStateType,
            organisationCivilianType,
            organisationMilitaryType,
            organisationPoliceType,
          ],
          'name',
        ),
        organizationTypesUniqueIds: parseMultipleDictionaries(
          [
            organisationStateType,
            organisationCivilianType,
            organisationMilitaryType,
            organisationPoliceType,
          ],
          'name',
        ),
        stateUniqueId: prepareFilters(state, 'uniqueId', 'name'),
        legalityUniqueId: prepareFilters(legality, 'uniqueId', 'name'),
        ancillaryTypeUniqueId: parseMultipleDictionaries(
          [artAccessoryType, artAmmunitionType, artComponentType],
          'name',
        ),
        hostTypeUniqueId: prepareFilters(artAccessoryHostType, 'uniqueId', 'name'),
        magnificationUniqueId: prepareFilters(artAccessoryMagnification, 'uniqueId', 'name'),
        utilityUniqueId: prepareFilters(artAccessoryUtility, 'uniqueId', 'name'),
        reticleUniqueId: prepareFilters(artAccessoryReticle, 'uniqueId', 'name'),
        lightColourUniqueId: prepareFilters(artAccessoryLightColour, 'uniqueId', 'name'),
        compositionUniqueId: prepareFilters(
          mergeDictionaries([artComposition, artAccessoryComposition]),
          'uniqueId',
          'name',
        ),
        proofHouseUniqueId: prepareFilters(
          mergeDictionaries([artAccessoryProofHouse, artComponentProofHouse]),
          'uniqueId',
          'name',
        ),
        calibreUniqueId: prepareFilters(
          mergeDictionaries([
            artAccessoryCalibre,
            artAmmunitionCalibreGauge,
            artComponentCalibre,
          ]),
          'uniqueId',
          'name',
        ),
        factoryUniqueId: prepareFilters(artAmmunitionFactory, 'uniqueId', 'name'),
        munitionVelocityUniqueId: prepareFilters(
          artAmmunitionMunitionVelocity,
          'uniqueId',
          'name',
        ),
        cartridgeCompositionUniqueId: prepareFilters(
          artAmmunitionCartridgeComposition,
          'uniqueId',
          'name',
        ),
        colourOfPrimerSealantUniqueId: prepareFilters(
          artAmmunitionColourOfPrimerSealant,
          'uniqueId',
          'name',
        ),
        shotSizeUniqueId: prepareFilters(artAmmunitionShotSize, 'uniqueId', 'name'),
        munitionTypeUniqueId: prepareFilters(artAmmunitionMunitionType, 'uniqueId', 'name'),
        ancillaryDictionaryUniqueId: prepareFilters(artAncillaryOptions, 'uniqueId', 'name'),
        artComponentManufacturer: prepareFilters(artComponentManufacturer, 'uniqueId', 'name'),
        artAmmunitionManufacturer: prepareFilters(
          artAmmunitionManufacturer,
          'uniqueId',
          'name',
        ),
        artAccessoryManufacturerMagazine: prepareFilters(
          artAccessoryManufacturerMagazine,
          'uniqueId',
          'name',
        ),
        artAccessoryManufacturerLaserLightModule: prepareFilters(
          artAccessoryManufacturerLaserLightModule,
          'uniqueId',
          'name',
        ),

        artAccessoryManufacturerOpticalSight: prepareFilters(
          artAccessoryManufacturerOpticalSight,
          'uniqueId',
          'name',
        ),
        artAccessoryManufacturerFlashEliminator: prepareFilters(
          artAccessoryManufacturerFlashEliminator,
          'uniqueId',
          'name',
        ),
        artAccessoryManufacturerSuppressor: prepareFilters(
          artAccessoryManufacturerSuppressor,
          'uniqueId',
          'name',
        ),
        functionalTypeUniqueId: prepareFilters(
          artAmmunitionFunctionalType,
          'uniqueId',
          'name',
        ),
        projectileShapeUniqueId: prepareFilters(
          artAmmunitionProjectileShape,
          'uniqueId',
          'name',
        ),
        transactionTypeUniqueId: prepareFilters(transactionType, 'uniqueId', 'name'),
        modulesUniqueIds: prepareFilters(auditLogModule, 'uniqueId', 'name'),
        eventTypesUniqueIds: prepareFilters(auditLogEventType, 'uniqueId', 'name'),
        eventNamesUniqueIds: prepareFilters(auditLogEventName, 'uniqueId', 'name'),
        statusUniqueId: prepareFilters(
          mergeDictionaries([legalStatus, weaponLicenceStatus, weaponLicenceTemplateStatus]),
          'uniqueId',
          'name',
        ),
        legalTypeUniqueId: prepareFilters(legalType, 'uniqueId', 'name'),
        legalTypes: prepareFilters(legalType, 'uniqueId', 'name'),
        hostTypesUniqueIds: prepareFilters(artAccessoryHostType, 'uniqueId', 'name'),
        magnificationsUniqueIds: prepareFilters(artAccessoryMagnification, 'uniqueId', 'name'),
        reticlesUniqueIds: prepareFilters(artAccessoryReticle, 'uniqueId', 'name'),
        utilitiesUniqueIds: prepareFilters(artAccessoryUtility, 'uniqueId', 'name'),
        calibresUniqueIds: prepareFilters(
          mergeDictionaries([
            artAccessoryCalibre,
            artAmmunitionCalibreGauge,
            artComponentCalibre,
          ]),
          'uniqueId',
          'name',
        ),
        compositionsUniqueIds: prepareFilters(
          mergeDictionaries([artComposition, artAccessoryComposition]),
          'uniqueId',
          'name',
        ),
        lightColoursUniqueIds: prepareFilters(artAccessoryLightColour, 'uniqueId', 'name'),
        coloursOfPrimerSealantUniqueIds: prepareFilters(
          artAmmunitionColourOfPrimerSealant,
          'uniqueId',
          'name',
        ),
        factoriesUniqueIds: prepareFilters(artAmmunitionFactory, 'uniqueId', 'name'),
        functionalTypesUniqueIds: prepareFilters(
          artAmmunitionFunctionalType,
          'uniqueId',
          'name',
        ),
        projectileShapesUniqueIds: prepareFilters(
          artAmmunitionProjectileShape,
          'uniqueId',
          'name',
        ),
        statesUniqueIds: prepareFilters(state, 'uniqueId', 'name'),
        manufacturersUniqueIds: prepareFilters(artComponentManufacturer, 'uniqueId', 'name'),
        transactionTypesUniqueIds: prepareFilters(transactionType, 'uniqueId', 'name'),
        statusesUniqueIds: prepareFilters(weaponLicenceStatus, 'uniqueId', 'name'),
        componentCapacitiesUniqueIds: prepareFilters(artComponentCapacity, 'uniqueId', 'name'),
        actions: auditLog.filteredByObjectValue,
        assignedUsersUniqueIds: prepareFilters(userOptions, 'value', 'label'),
        caseStatuses: prepareFilters(weaponLicenceCaseStatus, 'uniqueId', 'name'),
        licenceAuthorities: prepareFilters(weaponLicencingAuthority, 'uniqueId', 'name'),
        processTypes: prepareFilters(caseProcessTypes, 'uniqueId', 'name'),
        statuses: auditLog.filteredByObjectValue,
        caseStatusesUniqueIds: prepareFilters(weaponLicenceCaseStatus, 'uniqueId', 'name'),
        licenceAuthoritiesUniqueIds: prepareFilters(
          weaponLicencingAuthority,
          'uniqueId',
          'name',
        ),
        statusUniqueIds: prepareFilters(
          mergeDictionaries([weaponLicencePrintStatus, weaponLicencePrintBatchStatus]),
          'uniqueId',
          'name',
        ),
        frtMakesUniqueIds: prepareFilters(make, 'uniqueId', 'name'),
        frtManufacturersUniqueIds: prepareFilters(manufacturer, 'uniqueId', 'name'),
        frtModelsUniqueIds: prepareFilters(model, 'uniqueId', 'name'),
        frtTypesUniqueIds: prepareFilters(firearmType, 'uniqueId', 'name'),
      });
    }
  }, [
    artAccessoryCalibre,
    artAccessoryComposition,
    artAccessoryHostType,
    artAccessoryLightColour,
    artAccessoryMagnification,
    artAccessoryManufacturerFlashEliminator,
    artAccessoryManufacturerLaserLightModule,
    artAccessoryManufacturerMagazine,
    artAccessoryManufacturerOpticalSight,
    artAccessoryManufacturerSuppressor,
    artAccessoryProofHouse,
    artAccessoryReticle,
    artAccessoryType,
    artAccessoryUtility,
    artAmmunitionManufacturer,
    artAmmunitionType,
    artAncillaryOptions,
    artComponentManufacturer,
    artComponentType,
    auditLog,
    auditLogEventType,
    weaponLicenceStatus,
    auditLogModule,
    gender,
    legalEntityPrefix,
    legalVisibility,
    legality,
    organisationCategory,
    organisationCivilianType,
    organisationMilitaryType,
    organisationPoliceType,
    organisationStateType,
    state,
    transactionType,
    userOptions,
    legalStatus,
    artComponentCapacity,
    artComposition,
    weaponLicenceTemplateStatus,
    weaponLicenceCaseStatus,
    weaponLicencingAuthority,
    caseProcessTypes,
    weaponLicencePrintStatus,
    weaponLicencePrintBatchStatus,
    make,
    manufacturer,
    model,
    firearmType,
  ]);

  useEffect(() => {
    (async () => {
      getProperties();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
  }, []);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    [],
  );

  useEffect(() => {
    clearAlert(ActionTypes.CLEAR_AUDIT_LOG_SAME_PAGE_ALERT);
  }, []);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_AUDIT_LOG_SAME_PAGE_ALERT);
    },
    [],
  );

  useEffect(() => {
    if (id) {
      (async () => {
        await getAuditLogItemData(id);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const renderDetails = () => {
    if (
      auditLog &&
      (auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_LE_INDIVIDUAL ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_LE_ORGANIZATION ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_LE_INDIVIDUAL_CONTACT ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_LE_ORGANIZATION_CONTACT)
    ) {
      return <LegalEntitiesEventDetails auditLog={auditLog} />;
    }
    if (
      auditLog &&
      (auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_LE_PHOTO_GALLERY ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_FIREARM_PHOTO_GALLERY ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_ANCILLAIES_PHOTO_GALLERY ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DOWNLOAD_ANCILLAIES_PHOTO_GALLERY ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DOWNLOAD_FIREARM_PHOTO_GALLERY ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DOWNLOAD_LE_PHOTO_GALLERY)
    ) {
      return <PhotoGalleryEventDetails auditLog={auditLog} />;
    }
    if (auditLog && auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_CUSTOM_FIELD) {
      return <CustomFieldEventDetails auditLog={auditLog} />;
    }
    if (auditLog && auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_API_KEY) {
      return <ApiKeyEventDetails auditLog={auditLog} />;
    }
    if (auditLog && auditLog?.eventNameUniqueId === EAuditLogEvents.DISABLE_FIREARM) {
      return <FirearmEventDetails auditLog={auditLog} />;
    }
    if (
      auditLog &&
      (auditLog?.eventNameUniqueId ===
        EAuditLogEvents.EDIT_SUB_HEADER_ON_LOGIN_PAGE_AND_LOGOS_SETUP ||
        auditLog?.eventNameUniqueId ===
          EAuditLogEvents.EDIT_HEADER_ON_LOGIN_PAGE_AND_LOGOS_SETUP ||
        auditLog?.eventNameUniqueId ===
          EAuditLogEvents.EDIT_LOGO_1_ON_LOGIN_PAGE_AND_LOGOS_SETUP ||
        auditLog?.eventNameUniqueId ===
          EAuditLogEvents.EDIT_LOGO_2_ON_LOGIN_PAGE_AND_LOGOS_SETUP ||
        auditLog?.eventNameUniqueId ===
          EAuditLogEvents.EDIT_LOGO_3_ON_LOGIN_PAGE_AND_LOGOS_SETUP)
    ) {
      return <AdministrationLoginLogosEventDetails auditLog={auditLog} />;
    }
    if (auditLog && auditLog?.eventNameUniqueId === EAuditLogEvents.NEW_PASSWORD_GENERATION) {
      return <AdministrationPasswordChange auditLog={auditLog} />;
    }
    if (
      auditLog &&
      (auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_DATE_FORMAT ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_MINUTES_IN_BAN ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_INVALID_LOGIN_ATTEMPTS ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DEACTIVATE_TWO_FACTOR_AUTHENTICATION ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.ACTIVATE_TWO_FACTOR_AUTHENTICATION ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DEACTIVATE_AUTHENTICATOR ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.ACTIVATE_AUTHENTICATOR ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.ACTIVATE_E_TRACE_AUTO_GENERATION ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DEACTIVATE_E_TRACE_AUTO_GENERATION ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.ACTIVATE_I_ARMS_AUTO_GENERATION ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_MEASUREMENT ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_CURRENCY ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.DEACTIVATE_I_ARMS_AUTO_GENERATION ||
        auditLog?.eventNameUniqueId ===
          EAuditLogEvents.EDIT_RENEWING_PERIOD_BEFORE_LICENCE_EXPIRATION ||
        auditLog?.eventNameUniqueId ===
          EAuditLogEvents.EDIT_NOTIFICATIONS_FOR_UPCOMING_LICENSE_EXPIRATION)
    ) {
      return <AdministrationSystemSettingsEventDetails auditLog={auditLog} />;
    }
    if (
      auditLog &&
      auditLog?.eventNameUniqueId === EAuditLogEvents.EDIT_TWO_FACTOR_CHARTS_TIME_PERIOD
    ) {
      return <AdministrationSystemSettingsEventDetails auditLog={auditLog} />;
    }
    if (auditLog && auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_USER) {
      return <AdministrationUserEventDetails auditLog={auditLog} />;
    }
    if (auditLog && auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_ROLE) {
      return <AdministrationRoleEventDetails auditLog={auditLog} />;
    }
    if (
      auditLog &&
      auditLog?.eventNameUniqueId === EAuditLogEvents.DELETE_LICENCE_DEFINITION
    ) {
      return <LicencesEventDetails auditLog={auditLog} />;
    }
    if (
      auditLog &&
      (auditLog?.eventNameUniqueId === EAuditLogEvents.LE_INDIVIDUALS_GLOBAL_SEARCH ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.LE_ORGANIZATIONS_GLOBAL_SEARCH ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.FIREARMS_GLOBAL_SEARCH ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.TRANSACTIONS_GLOBAL_SEARCH ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.ACCESSORIES_GLOBAL_SEARCH ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.AMMUNITION_GLOBAL_SEARCH ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.COMPONENTS_GLOBAL_SEARCH ||
        auditLog?.eventNameUniqueId === EAuditLogEvents.USERS_GLOBAL_SEARCH)
    ) {
      return <GlobalSearchEventDetails auditLog={auditLog} />;
    }
    if (!auditLog?.objectUniqueId) {
      return null;
    }
    switch (auditLog.moduleUniqueId) {
      case AuditLogModules.FIREARMS:
        return <FirearmEventDetails auditLog={auditLog} />;

      case AuditLogModules.ANCILLARIES:
        return <AncillariesEventDetails auditLog={auditLog} />;

      case AuditLogModules.TRANSACTIONS:
        return <TransactionEventDetails auditLog={auditLog} />;

      case AuditLogModules.CUSTOM_FIELDS:
        return <CustomFieldEventDetails auditLog={auditLog} />;

      case AuditLogModules.ADMINISTRATION:
        if (auditLog?.objectType === AdministrationObjectTypes.ROLE) {
          return <AdministrationRoleEventDetails auditLog={auditLog} />;
        }
        if (auditLog?.objectType === AdministrationObjectTypes.DICTIONARY) {
          return <AdministrationDictionaryEventDetails auditLog={auditLog} />;
        }
        if (auditLog?.objectType === AdministrationObjectTypes.USER) {
          return <AdministrationUserEventDetails auditLog={auditLog} />;
        }
        return null;

      case AuditLogModules.API_KEYS:
        return <ApiKeyEventDetails auditLog={auditLog} />;

      case AuditLogModules.LEGAL_ENTITIES:
        return <LegalEntitiesEventDetails auditLog={auditLog} />;

      case AuditLogModules.WEAPON_LICENCE_CONFIGURATOR:
        if (
          auditLog.eventNameUniqueId === EAuditLogEvents.ADD_APPLICATION_PROCESS ||
          auditLog.eventNameUniqueId === EAuditLogEvents.EDIT_APPLICATION_PROCESS ||
          auditLog.eventNameUniqueId === EAuditLogEvents.ACTIVATE_APPLICATION_PROCESS ||
          auditLog.eventNameUniqueId === EAuditLogEvents.DEACTIVATE_APPLICATION_PROCESS
        ) {
          return <LicencesApplicationProcessEventDetails auditLog={auditLog} />;
        } else {
          return <LicencesEventDetails auditLog={auditLog} />;
        }

      case AuditLogModules.WEAPON_LICENCES:
        if (auditLog?.objectType === LicencesObjectTypes.WEAPON_LICENCE_PRINTOUT) {
          return <LegalEntityLicencesPrintoutEventDetails auditLog={auditLog} />;
        }
        if (auditLog?.objectType === LicencesObjectTypes.WEAPON_LICENCE_PRINT_BATCH) {
          return <LegalEntityLicencesPrintoutBatchEventDetails auditLog={auditLog} />;
        }
        if (auditLog?.objectType === LicencesObjectTypes.LICENCE_AUTHORITY) {
          return <LegalEntityLicencesAuthorityEventDetails auditLog={auditLog} />;
        }
        return <LegalEntityLicencesEventDetails auditLog={auditLog} />;
      default:
        return null;
    }
  };

  const getFilterValue = (key: string) => {
    if (!auditLog) {
      return '';
    }

    const value = auditLog.filteredByObjectValue[key];
    const filtersToCapitalize = ['statuses'];

    if (dictionaries?.[key]) {
      if (Array.isArray(value)) {
        let mappedFilter = value.map((item) => dictionaries[key]?.[item] ?? item);
        if (filtersToCapitalize.includes(key))
          mappedFilter = mappedFilter.map((item) => capitalizeFirstLetter(item));
        return mappedFilter.join(', ');
      }

      if (typeof value === 'string' && value.includes(',')) {
        return value
          .split(',')
          .map((item) => item.trim())
          .map((item) => dictionaries[key]?.[item] ?? item)
          .join(', ');
      }

      return dictionaries[key]?.[value] ?? value;
    }

    if (
      [
        'transactionTimeFrom',
        'transactionTimeTo',
        'createTimeFrom',
        'createTimeTo',
        'expirationTimeFrom',
        'expirationTimeTo',
        'birthdayFrom',
        'birthdayTo',
        'eventDateFrom',
        'eventDateTo',
        'transactionDateFrom',
        'transactionDateTo',
        'expiryDateFrom',
        'expiryDateTo',
        'manufactureDateFrom',
        'manufactureDateTo',
        'requestDateFrom',
        'requestDateTo',
      ].includes(key)
    ) {
      return formatDate(auditLog.filteredByObjectValue[key], shortDateFormat);
    }
    if (key === 'manufacturerUniqueId') {
      if (
        auditLog.filteredByObjectValue.ancillaryDictionaryUniqueId ===
        EArtAncillaryType.ACCESSORIES
      ) {
        return getAccessoryManufacturerDictionaryData(
          auditLog.filteredByObjectValue.ancillaryTypeUniqueId,
          auditLog.filteredByObjectValue[key],
        );
      }
      if (
        auditLog.filteredByObjectValue.ancillaryDictionaryUniqueId ===
        EArtAncillaryType.AMMUNITION
      ) {
        return dictionaries?.artAmmunitionManufacturer?.[auditLog.filteredByObjectValue[key]];
      }
      if (
        auditLog.filteredByObjectValue.ancillaryDictionaryUniqueId ===
        EArtAncillaryType.COMPONENTS
      ) {
        return dictionaries?.artComponentManufacturer?.[auditLog.filteredByObjectValue[key]];
      }
    }
    return auditLog.filteredByObjectValue[key];
  };

  const filterIsDisabled = (key: string) =>
    [
      'ownerUniqueId',
      'keeperUniqueId',
      'ownerType',
      'keeperType',
      'customFields',
      'isKeeperHyperLinkActive',
      'isOwnerHyperLinkActive',
      'keeperVisibilityUniqueId',
      'ownerVisibilityUniqueId',
      'ancillaryDictionaryUniqueId',
      'firearmUniqueId',
      'regions',
      'rolesUniqueIds',
    ].includes(key);

  const renderCustomFieldData = (customField: any) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.bool === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      const fromDate = customField.dateFrom
        ? formatDate(customField.dateFrom, shortDateFormat)
        : null;
      const toDate = customField.dateTo
        ? formatDate(customField.dateTo, shortDateFormat)
        : null;
      return `${fromDate ?? ''} ${toDate && fromDate ? '-' : ''} ${toDate ?? ''}`;
    }
    if (customField.customFieldType === ECustomFieldType.TEXT) {
      return customField.text;
    }
    return customField.value;
  };

  const getCustomfields = () => {
    const customFields: any[] = [];
    // eslint-disable-next-line array-callback-return
    auditLog?.filteredByObjectValue?.customFields?.map((customField: any) => {
      const label =
        customField?.translations?.filter(
          (translation: any) => selectedLanguage?.uniqueId === translation.languageUniqueId,
        )?.[0]?.name ||
        customField?.translations?.filter(
          (translation: any) => defaultLanguage?.uniqueId === translation.languageUniqueId,
        )?.[0]?.name;

      const val = renderCustomFieldData(customField);

      if (label && val) {
        customFields.push({
          value: val,
          label,
          key: customField?.customFieldUniqueId,
        });
      }
    });
    return customFields;
  };

  const authorLicenceAuthorities = () => {
    if (!auditLog?.accountLicenceAuthorities) {
      return (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          -
        </Typography>
      );
    }

    if (auditLog?.accountLicenceAuthorities?.length > 1 && hasWeaponLicenceModule()) {
      return (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          <Chips
            text={t('auditLogDetails.common.licenceAuthorities.all')}
            variant={EChipsVariants.primary}
          />
        </Typography>
      );
    }

    return (
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
        <Chips
          text={auditLog?.accountLicenceAuthorities[0]}
          variant={EChipsVariants.primary}
        />
      </Typography>
    );
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('user.breadcrumbs.administration'), route: ERouteLinks.Administration },
          {
            label: t('auditLogDetails.breadcrumbs.auditLog'),
            route: ERouteLinks.AuditLogPage,
          },
          t('auditLogDetails.breadcrumbs.eventDetails'),
        ]}
      />
      <StyledPageTitle variant="h4">
        {t('auditLogDetails.common.eventDetails')}
      </StyledPageTitle>

      <Grid container>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Button
            fullWidth
            label={t('general.back.link')}
            variant={EButtonVariants.outlined}
            size={EButtonSizes.medium}
            icon={EIconTypes.left}
            onClick={() => navigate(ERouteLinks.AuditLogPage)}
          />
        </Grid>
      </Grid>

      {auditLog && (
        <>
          <AuditLogDetails container>
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.displayUniqueId.label')}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <StyledRouterLink
                  to={ERouteLinks.ViewUser.replace(':id', auditLog?.author?.uniqueId)}
                >
                  {emptyFieldHolder(auditLog?.author?.displayUniqueId)}
                </StyledRouterLink>
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('auditLogDetails.common.eventAuthor')}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {auditLog.author ? (
                  <StyledRouterLink
                    to={ERouteLinks.ViewUser.replace(':id', auditLog?.author?.uniqueId)}
                  >
                    {`${auditLog?.author?.firstName} ${auditLog?.author?.lastName}`}
                  </StyledRouterLink>
                ) : (
                  '-'
                )}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('auditLogDetails.common.roleName')}
              </Typography>
              <Typography variant="body2">
                {auditLog.accountRoles
                  ? auditLog.accountRoles.map((roleName: string) => (
                      <Chips text={roleName as string} variant={EChipsVariants.primary} />
                    ))
                  : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('auditLogDetails.common.ipAddress')}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {emptyFieldHolder(auditLog?.ipAddress)}
              </Typography>
            </Grid>
            {hasWeaponLicenceModule() && (
              <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('auditLogDetails.common.licenceAuthorities')}
                </Typography>
                {authorLicenceAuthorities()}
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('auditLogDetails.common.module')}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {emptyFieldHolder(auditLog?.module)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('auditLogDetails.common.eventType')}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {auditLog?.eventType ? (
                  <Chips
                    text={auditLog.eventType}
                    variant={getEventTypeColor(auditLog.eventTypeUniqueId as EventTypes)}
                  />
                ) : (
                  '-'
                )}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('auditLogDetails.common.eventName')}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {emptyFieldHolder(auditLog.eventName)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('auditLogDetails.common.eventDate')}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {formatDate(auditLog.eventDate, longDateFormat)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('auditLogDetails.common.trustedDeviceName')}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {emptyFieldHolder(auditLog?.trustedDeviceName)}
              </Typography>
            </Grid>

            {auditLog.filteredByObjectValue && (
              <Grid size={{ xs: 12, md: 3 }} sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('auditLogDetails.common.filteredByObjectValue')}
                </Typography>
                <StyledFilterChipWrapper>
                  {Object.keys(auditLog.filteredByObjectValue)
                    .filter(
                      (key: string) =>
                        auditLog.filteredByObjectValue[key] &&
                        auditLog.filteredByObjectValue[key].length &&
                        !filterIsDisabled(key),
                    )
                    .map((key: string) => (
                      <FilterChip
                        key={key}
                        label={t(validLabels?.[key] || key)}
                        values={getFilterValue(key)}
                      />
                    ))
                    .sort((a, b) => a.props.label.localeCompare(b.props.label))}
                </StyledFilterChipWrapper>
                <StyledFilterChipWrapper>
                  {getCustomfields().map((item) => (
                    <FilterChip key={item.key} label={item.label} values={item.value} />
                  ))}
                </StyledFilterChipWrapper>
              </Grid>
            )}
          </AuditLogDetails>
          {renderDetails()}
          {samePageAlert && (
            <Box sx={{ marginBottom: '16px' }}>
              <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
            </Box>
          )}
          {auditLogSamePageAlert && (
            <Box sx={{ marginBottom: '16px' }}>
              <Alert
                text={auditLogSamePageAlert.text}
                variant={auditLogSamePageAlert.variant}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default AuditLogViewPage;
